(function () {
  // Debug helper — set MEDIA_GALLERY_DEBUG to false in production.
  // Logs are written at console.debug level so browsers hide them by
  // default; filter with "[mg]" prefix when investigating.
  var MEDIA_GALLERY_DEBUG = false;
  var MG = MEDIA_GALLERY_DEBUG
    ? function () {
        console.debug.apply(console, ["[mg]"].concat(Array.prototype.slice.call(arguments)));
      }
    : function () {};

  MG("script evaluating");

  // Always define the class so we can use its prototype methods even if
  // the custom element is already registered (e.g. after an HMR reload).
  var alreadyDefined = !!customElements.get("media-gallery");
  if (alreadyDefined) {
    MG("media-gallery already registered (HMR reload?) — will force-reinitialize existing elements");
  } else {
    MG("defining <media-gallery> custom element");
  }

  class MediaGallery extends HTMLElement {
    constructor() {
      super();

      var elId = this.getAttribute("id") || this.dataset.sectionId || "(no id)";
      MG("constructor", elId);

      if (typeof Shopify === "undefined") {
        MG("Shopify global missing — delaying init by 300ms", elId);
        setTimeout(this.init.bind(this), 300);
      } else if (Shopify.designMode) {
        MG("design mode — delaying init by 200ms", elId);
        setTimeout(() => this.init(), 200);
      } else {
        MG("calling init() immediately", elId);
        this.init();
      }
    }

    disconnectedCallback() {
      window.removeEventListener("on:debounced-resize", this.resizeHandler);

      if (this.resizeInitHandler) {
        window.removeEventListener(
          "on:debounced-resize",
          this.resizeInitHandler,
        );
      }

      if (this.zoomInitHandler) {
        window.removeEventListener("on:debounced-resize", this.zoomInitHandler);
      }
    }

    init() {
      MG("init() start", this.dataset.sectionId || "(no sectionId)",
        this._initialized ? "(REINIT)" : "(first init)");

      // ---- Reinitialization safety: clean up previous listeners ----
      if (this._initialized) {
        if (this._onVariantChangeBound && this.section) {
          this.section.removeEventListener(
            "on:variant:change",
            this._onVariantChangeBound,
          );
        }
        // Allow initGallery to rebind its slider listeners.
        this._galleryListenersAdded = false;
      }

      this.section = this.closest(".js-product");
      if (!this.section) {
        console.warn(
          "[mg] init() — no .js-product ancestor found for",
          this,
          ". The gallery will not respond to variant changes. Is the section wrapper missing the js-product class?",
        );
      } else {
        MG("found .js-product ancestor");
      }

      this.noSelectedVariant = this.hasAttribute("data-no-selected-variant");
      this.mediaGroupingEnabled =
        this.hasAttribute("data-media-grouping-enabled") &&
        this.getMediaGroupData();
      this.variantImageListEnabled = this.hasAttribute("data-variant-image-list-enabled");
      MG("features — mediaGrouping:", this.mediaGroupingEnabled,
        "variantImageList:", this.variantImageListEnabled,
        "layout:", this.dataset.layout);
      this.stackedScroll = this.dataset.stackedScroll;
      this.stackedUnderline =
        this.dataset.stackedUnderline === "true" && !this.mediaGroupingEnabled;
      this.isFeatured = this.dataset.isFeatured === "true";
      this.viewer = this.querySelector(".media-viewer");
      this.thumbs = this.querySelector(".media-thumbs");
      this.thumbsItems = this.querySelectorAll(".media-thumbs__item");
      this.controls = this.querySelector(".media-ctrl");
      this.prevBtn = this.querySelector('.media-ctrl__btn[name="prev"]');
      this.nextBtn = this.querySelector('.media-ctrl__btn[name="next"]');
      this.counterCurrent = this.querySelector(".media-ctrl__current-item");
      this.counterTotal = this.querySelector(".media-ctrl__total-items");
      this.liveRegion = this.querySelector(".media-gallery__status");
      this.zoomLinks = this.querySelectorAll(".js-zoom-link");
      this.loadingSpinner = this.querySelector(".loading-spinner");
      this.xrButton = this.querySelector(".media-xr-button");

      MG("DOM query — viewer:", !!this.viewer, "thumbs:", !!this.thumbs,
        "controls:", !!this.controls, "zoomLinks:", this.zoomLinks.length);

      if (this.hasAttribute("data-zoom-enabled")) {
        this.galleryModal = this.querySelector(
          ".js-media-zoom-template",
        ).content.firstElementChild.cloneNode(true);
      }

      if (this.mediaGroupingEnabled && !this.noSelectedVariant) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }

      if (this.variantImageListEnabled) {
        MG("VIL enabled — querying viewer items and data");
        this.mediaGroupingEnabled = false;
        this.viewerItems = this.querySelectorAll(".media-viewer__item");
        this.variantImageListData = this.getVariantImageListData();
        MG("VIL — viewerItems:", this.viewerItems.length,
          "variantImageListData:", !!this.variantImageListData,
          "initialVariantId:", this.dataset.initialVariantId);
        // Store original padding-top for VIL images so we can restore and
        // normalise heights when switching variants.
        this.viewerItems.forEach((item) => {
          if (item.classList.contains("variant-image-list-item--metafield")) {
            const mediaDiv = item.querySelector(".media");
            if (mediaDiv) {
              mediaDiv.dataset.originalPaddingTop = mediaDiv.style.paddingTop;
            }
          }
        });
        // Always filter to the initial variant on page load (first variant
        // or the variant from the URL query parameter if present).
        const initialVariantId = this.dataset.initialVariantId;
        if (initialVariantId) {
          this.setActiveVariantImages(parseInt(initialVariantId, 10));
        }
        // Always initialise gallery controls/listeners after visibility setup
        this.initGallery();
        MG("VIL init complete — initGallery called");
      }

      if (this.dataset.layout === "stacked" && theme.mediaMatches.md) {
        MG("stacked layout on md+");
        this.resizeInitHandler =
          this.resizeInitHandler || this.initGallery.bind(this);
        window.addEventListener("on:debounced-resize", this.resizeInitHandler);
        this.setVisibleItems();
        this.previousMediaItem = this.querySelector(
          ".media-viewer__item.is-current-variant",
        );
        setTimeout(
          () =>
            this.customSetActiveMedia(
              this.previousMediaItem,
              this.stackedScroll === "always",
            ),
          200,
        );
      } else if (!this.variantImageListEnabled) {
        MG("non-VIL, non-stacked — calling initGallery");
        this.initGallery();
      } else {
        MG("VIL already handled initGallery, skipping duplicate");
      }

      if (this.zoomLinks && this.zoomLinks.length && !this._zoomListenersAdded) {
        MG("setting up zoom");
        this._zoomListenersAdded = true;
        if (this.dataset.zoomTrigger === "hover") {
          this.triggerZoomInit();
        } else {
          this.zoomLinks.forEach((zoomLink) => {
            zoomLink.addEventListener("click", (evt) => {
              this.triggerZoomInit();
              evt.preventDefault();
            });
          });
        }
      }

      // Store the bound handler so we can remove it on reinit.
      if (!this._onVariantChangeBound) {
        this._onVariantChangeBound = this.onVariantChange.bind(this);
      }
      if (this.section) {
        this.section.addEventListener(
          "on:variant:change",
          this._onVariantChangeBound,
        );
        MG("bound on:variant:change listener");
      } else {
        console.warn(
          "[mg] init() — cannot bind variant change listener because this.section is null.",
          "The gallery will be static.",
        );
      }

      this._initialized = true;
      MG("init() complete — readyState:", document.readyState);
    }

    triggerZoomInit() {
      this.zoomInitHandler = this.zoomInitHandler || this.initZoom.bind(this);
      this.zoomEventListener =
        this.zoomEventListener || this.handleZoomMouseMove.bind(this);
      window.addEventListener("on:debounced-resize", this.zoomInitHandler);
      this.initZoom();
      this.zoomLinks.forEach((zoomLink) => {
        zoomLink.addEventListener("click", (evt) => {
          evt.preventDefault();
        });
      });
    }

    /**
     * Handle a change in variant on the page.
     * @param {Event} evt - variant change event dispatched by variant-picker
     */
    onVariantChange(evt) {
      MG("onVariantChange — variant:",
        evt.detail.variant ? evt.detail.variant.id : null,
        "VIL:", this.variantImageListEnabled,
        "mediaGrouping:", this.mediaGroupingEnabled);
      if (this.variantImageListEnabled) {
        this.setActiveVariantImages(
          evt.detail.variant ? evt.detail.variant.id : null,
        );
        this.initGallery();
        return;
      }

      if (this.mediaGroupingEnabled) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }

      if (evt.detail.variant && evt.detail.variant.featured_media) {
        const variantMedia = this.viewer.querySelector(
          `[data-media-id="${evt.detail.variant.featured_media.id}"]`,
        );
        if (variantMedia) {
          this.customSetActiveMedia(variantMedia, true);
        }
      }
    }

    /**
     * Gets the media group from currently selected variant options.
     * @returns {?object}
     */
    getMediaGroupFromOptionSelectors() {
      const optionSelectors = this.section.querySelectorAll(".option-selector");
      if (optionSelectors.length > this.getMediaGroupData().groupOptionIndex) {
        const selector =
          optionSelectors[this.getMediaGroupData().groupOptionIndex];
        if (selector.dataset.selectorType === "dropdown") {
          return selector
            .querySelector(".custom-select__btn")
            .textContent.trim();
        }
        return selector.querySelector("input:checked").value;
      }
      return null;
    }

    /**
     * Gets the variant media associations for a product.
     * @returns {?object}
     */
    getMediaGroupData() {
      if (typeof this.variantMediaData === "undefined") {
        const dataEl = this.querySelector(".js-data-variant-media");
        this.variantMediaData = dataEl ? JSON.parse(dataEl.textContent) : false;
      }

      return this.variantMediaData;
    }

    /**
     * Gets the variant image list data for this product.
     * @returns {?object}
     */
    getVariantImageListData() {
      if (typeof this._variantImageListData === "undefined") {
        const dataEl = this.querySelector(".js-data-variant-image-list");
        this._variantImageListData = dataEl ? JSON.parse(dataEl.textContent) : false;
      }
      return this._variantImageListData;
    }

    /**
     * Show only the product's featured image (when no variant is selected).
     */
    showOnlyFeaturedImage() {
      const data = this.getVariantImageListData();
      if (!data || !data.featuredMediaId) return;

      const featuredId = data.featuredMediaId;
      this.viewerItems.forEach((item) => {
        if (
          parseInt(item.dataset.mediaId, 10) === featuredId &&
          !item.classList.contains("variant-image-list-item--metafield")
        ) {
          item.hidden = false;
        } else {
          item.hidden = true;
        }
      });

      if (this.thumbsItems) {
        this.thumbsItems.forEach((thumb) => {
          const isMetafieldThumb = thumb.classList.contains(
            "variant-image-list-thumb--metafield",
          );
          const regularMediaId = parseInt(thumb.dataset.mediaId, 10);
          if (isMetafieldThumb) {
            thumb.hidden = true;
          } else {
            thumb.hidden = regularMediaId !== featuredId;
          }
        });
      }

      this.setVisibleItems();
      const featuredItem = this.viewer.querySelector(
        `.media-viewer__item[data-media-id="${featuredId}"]`,
      );
      if (featuredItem && !featuredItem.hidden) {
        if (
          !this.currentItem ||
          this.currentItem.dataset.mediaId !== String(featuredId)
        ) {
          this.customSetActiveMedia(featuredItem, true);
        }
      }

      // Handle single-image styling
      this.viewerItems.forEach((item) => {
        item.classList.remove("media-viewer__item--single");
      });
      if (this.visibleItems.length === 1 && this.currentItem) {
        this.currentItem.classList.add("media-viewer__item--single");
      }

      // Re-init gallery called externally after this method
    }

    /**
     * Replace gallery images with the selected variant's featured image and
     * variant_image_list images for the selected variant.
     * @param {?number} variantId - The selected variant ID, or null/undefined if none selected.
     */
    setActiveVariantImages(variantId) {
      if (!variantId) {
        this.showOnlyFeaturedImage();
        return;
      }

      const data = this.getVariantImageListData();
      if (!data) return;

      const variantData = data.variantMedia.find(
        (v) => v.variantId === variantId,
      );
      const featuredMediaId = variantData
        ? parseInt(variantData.featuredMediaId, 10)
        : null;
      const variantIdStr = String(variantId);

      // Determine whether this variant has its own VIL images (metafield
      // images assigned to it).  If it does, we hide the Shopify-native
      // variant featured image entirely so only the VIL images are shown.
      let hasVilForVariant = false;
      this.viewerItems.forEach((item) => {
        if (
          item.classList.contains("variant-image-list-item--metafield") &&
          item.dataset.variantId === variantIdStr
        ) {
          hasVilForVariant = true;
        }
      });

      let firstVisibleItem = null;

      this.viewerItems.forEach((item) => {
        const isMetafieldImage = item.classList.contains(
          "variant-image-list-item--metafield",
        );
        const itemMediaId = parseInt(item.dataset.mediaId, 10);
        const itemVariantId = item.dataset.variantId;

        if (isMetafieldImage) {
          // Show only metafield images for this variant
          if (itemVariantId === variantIdStr) {
            item.hidden = false;
            if (!firstVisibleItem) {
              firstVisibleItem = item;
            }
          } else {
            item.hidden = true;
          }
        } else {
          // When the selected variant has its own VIL images, hide the
          // Shopify-native featured variant image.  Otherwise fall back
          // to showing the native featured media as before.
          if (hasVilForVariant) {
            item.hidden = true;
          } else if (featuredMediaId && itemMediaId === featuredMediaId) {
            item.hidden = false;
            if (!firstVisibleItem) {
              firstVisibleItem = item;
            }
          } else {
            item.hidden = true;
          }
        }
      });

      if (this.thumbsItems) {
        this.thumbsItems.forEach((thumb) => {
          const isMetafieldThumb = thumb.classList.contains(
            "variant-image-list-thumb--metafield",
          );
          const thumbVariantId = thumb.dataset.variantId;
          const regularMediaId = parseInt(thumb.dataset.mediaId, 10);

          if (isMetafieldThumb) {
            thumb.hidden = thumbVariantId !== variantIdStr;
          } else if (hasVilForVariant) {
            // Hide the native thumbnail when VIL images are available
            thumb.hidden = true;
          } else {
            thumb.hidden = !(featuredMediaId && regularMediaId === featuredMediaId);
          }
        });
      }

      this.setVisibleItems();

      if (firstVisibleItem) {
        this.customSetActiveMedia(firstVisibleItem, true);
      }

      // Normalise the height of VIL images so that taller images are
      // constrained to the height of the shortest (widest) image, keeping
      // the gallery container stable regardless of which image is active.
      this._normaliseVilImageHeights();

      // If there's only one visible item, mark it as single
      const visibleCount = this.viewerItems.length
        ? Array.from(this.viewerItems).filter((el) => !el.hidden).length
        : 0;
      this.viewerItems.forEach((item) => {
        item.classList.remove("media-viewer__item--single");
      });
      if (visibleCount === 1 && this.currentItem) {
        this.currentItem.classList.add("media-viewer__item--single");
      }

      // Re-init gallery called externally after this method
    }

    /**
     * Normalise the container height of all visible VIL images to match
     * the shortest (widest) image so the gallery doesn't jump when
     * switching between portrait and landscape variant images.
     */
    _normaliseVilImageHeights() {
      if (!this.viewerItems) return;

      const visibleVilItems = Array.from(this.viewerItems).filter(
        (item) =>
          !item.hidden &&
          item.classList.contains("variant-image-list-item--metafield"),
      );
      if (visibleVilItems.length < 2) return;

      // First restore every VIL item to its original padding-top so we
      // read real values and don't compound previous normalisations.
      this.viewerItems.forEach((item) => {
        if (item.classList.contains("variant-image-list-item--metafield")) {
          const mediaDiv = item.querySelector(".media");
          if (mediaDiv && mediaDiv.dataset.originalPaddingTop !== undefined) {
            mediaDiv.style.paddingTop = mediaDiv.dataset.originalPaddingTop;
          }
        }
      });

      // Find the minimum padding-top (shortest container = widest image)
      // among the currently visible VIL items.
      let minPaddingTop = Infinity;
      visibleVilItems.forEach((item) => {
        const mediaDiv = item.querySelector(".media");
        if (!mediaDiv) return;
        const pt = parseFloat(mediaDiv.style.paddingTop);
        if (!isNaN(pt) && pt < minPaddingTop) {
          minPaddingTop = pt;
        }
      });

      if (minPaddingTop === Infinity) return;

      // Apply the minimum height to all visible VIL items (and hidden
      // ones too so they're ready when scrolled to).
      this.viewerItems.forEach((item) => {
        if (item.classList.contains("variant-image-list-item--metafield")) {
          const mediaDiv = item.querySelector(".media");
          if (mediaDiv) {
            mediaDiv.style.paddingTop = minPaddingTop + "%";
          }
        }
      });
    }

    /**
     * Gets an object mapping media to groups, and the reverse
     * @returns {?object}
     */
    getMediaGroupMap() {
      if (!this.mediaGroupMap) {
        this.mediaGroupMap = {
          groups: {},
        };

        // set up grouping
        const variantMediaData = this.getMediaGroupData();
        let currentMediaOptionName = false;
        this.viewerItems = this.querySelectorAll(".media-viewer__item");
        this.viewerItems.forEach((item) => {
          for (let i = 0; i < variantMediaData.variantMedia.length; i += 1) {
            if (
              parseInt(item.dataset.mediaId, 10) ===
              variantMediaData.variantMedia[i].mediaId
            ) {
              if (
                currentMediaOptionName !==
                variantMediaData.variantMedia[i].option
              ) {
                currentMediaOptionName =
                  variantMediaData.variantMedia[i].option;
              }
            }
          }
          if (currentMediaOptionName) {
            if (!this.mediaGroupMap.groups[currentMediaOptionName]) {
              this.mediaGroupMap.groups[currentMediaOptionName] = {
                name: currentMediaOptionName,
                items: [],
              };
            }
            const groupItem = { main: item };
            if (this.thumbs) {
              groupItem.thumb = this.thumbs.querySelector(
                `[data-media-id="${item.dataset.mediaId}"].media-thumbs__item`,
              );
            }
            this.mediaGroupMap.groups[currentMediaOptionName].items.push(
              groupItem,
            );
          }
        });

        // add helper
        this.mediaGroupMap.groupFromItem = (item) => {
          const groups = Object.keys(this.mediaGroupMap.groups);
          for (let i = 0; i < groups; i += 1) {
            const group = groups[i];
            for (
              let j = 0;
              j < this.mediaGroupMap.groups[group].items.length;
              j += 1
            ) {
              if (this.mediaGroupMap.groups[group].items[j] === item) {
                return this.mediaGroupMap.groups[group];
              }
            }
          }
          return this.mediaGroupMap.groups[
            Object.keys(this.mediaGroupMap.groups)[0]
          ];
        };
      }

      return this.mediaGroupMap;
    }

    /**
     * Show only images associated to the current variant
     * @param {string} groupName - optional - Group to show (uses this.currentItem if empty)
     */
    setActiveMediaGroup(groupName) {
      const mediaGroupMap = this.getMediaGroupMap();
      const selectedGroup = groupName
        ? mediaGroupMap.groups[groupName]
        : mediaGroupMap.groupFromItem(this.currentItem);

      if (selectedGroup) {
        if (this.currentGroup !== selectedGroup) {
          this.currentGroup = selectedGroup;
          this.viewerItems.forEach((item) => {
            item.style.display = "none";
            item.classList.remove("media-viewer__item--single");
          });
          this.thumbsItems.forEach((item) => {
            item.style.display = "none";
          });

          let currentItemIsVisible = false;
          selectedGroup.items.forEach((item) => {
            item.main.style.display = "";
            if (item.thumb) {
              item.thumb.style.display = "";
            }
            if (item.main === this.currentItem) {
              currentItemIsVisible = true;
            }
          });
          this.setVisibleItems();

          // If current item is not in this group, set it as the active item
          if (!currentItemIsVisible) {
            this.customSetActiveMedia(selectedGroup.items[0].main, true);
          }

          // Handle single images on stacked view
          if (selectedGroup.items.length === 1) {
            selectedGroup.items[0].main.classList.add(
              "media-viewer__item--single",
            );
          }
        }
      } else {
        this.viewerItems.forEach((item) => {
          item.style.display = "";
        });
        this.thumbsItems.forEach((item) => {
          item.style.display = "";
        });
      }
    }

    /**
     * Initialises the media gallery slider and associated controls.
     */
    initGallery() {
      this.setVisibleItems();

      // Always attach listeners, even for single-image galleries
      // (so the gallery works when more images appear after variant change).
      if (!this._galleryListenersAdded) {
        this._galleryListenersAdded = true;
        this.addListeners();
      }

      if (this.visibleItems.length <= 1) {
        if (this.currentItem && this.visibleItems.includes(this.currentItem)) {
          this.currentIndex = this.visibleItems.indexOf(this.currentItem);
        } else if (this.visibleItems.length === 1) {
          this.currentItem = this.visibleItems[0];
          this.currentIndex = 0;
        }
        this.updateControls();
        return;
      }

      this.viewerItemOffset =
        this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

      // Avoid divide-by-zero when items are not laid out yet.
      if (this.viewerItemOffset <= 0) {
        this.updateControls();
        return;
      }

      // When the variant image list is managing visibility we already set the
      // active item via customSetActiveMedia; trust it if it is still visible
      // instead of deriving the index from scrollLeft.
      if (
        this.variantImageListEnabled &&
        this.currentItem &&
        this.visibleItems.includes(this.currentItem)
      ) {
        this.currentIndex = this.visibleItems.indexOf(this.currentItem);
      } else {
        this.currentIndex = Math.round(
          this.viewer.scrollLeft / this.viewerItemOffset,
        );
        if (
          isNaN(this.currentIndex) ||
          this.currentIndex < 0 ||
          this.currentIndex >= this.visibleItems.length
        ) {
          this.currentIndex = 0;
        }
        this.currentItem = this.visibleItems[this.currentIndex];
      }

      if (this.thumbs && this.currentItem) {
        this.currentThumb = this.thumbs.querySelector(
          `[data-media-id="${this.currentItem.dataset.mediaId}"]`,
        );
      }

      if (!this.isFeatured && document.hasFocus()) {
        // Eager load only the first off-screen image for smooth UX.
        // Remaining images stay lazy-loaded via native browser behaviour.
        const lazyImages = this.viewer.querySelectorAll('.product-image[loading="lazy"]');
        if (lazyImages.length > 1) {
          lazyImages[1].loading = 'eager';
        }
      }

      // Don't override the active item when variant image list is managing visibility
      if (!this.variantImageListEnabled) {
        const currentItem = this.querySelector(
          ".media-viewer__item.is-current-variant",
        );
        this.customSetActiveMedia(currentItem, true);
      }

      this.updateControls();
    }

    /**
     * Updates the prev/next controls and counter based on the current state.
     */
    updateControls() {
      if (!this.controls) return;

      if (this.prevBtn) {
        this.prevBtn.disabled =
          this.currentIndex === 0 || this.visibleItems.length <= 1;
      }

      if (this.nextBtn) {
        this.nextBtn.disabled =
          this.currentIndex === this.visibleItems.length - 1 ||
          this.visibleItems.length <= 1;
      }

      if (this.counterCurrent) {
        this.counterCurrent.textContent = this.currentIndex + 1;
      }
    }

    addListeners() {
      if (this.viewer)
        this.viewer.addEventListener("scroll", this.handleScroll.bind(this));
      if (this.controls)
        this.controls.addEventListener("click", this.handleNavClick.bind(this));
      if (this.thumbs)
        this.thumbs.addEventListener("click", this.handleThumbClick.bind(this));
      this.resizeHandler = this.resizeHandler || this.handleResize.bind(this);
      window.addEventListener("on:debounced-resize", this.resizeHandler);
    }

    /**
     * Initialized the zoom on hover for desktop
     */
    initZoom() {
      this.zoomLinks.forEach((el) => {
        const zoomWidth = Number(
          el.querySelector(".zoom-image").dataset.originalWidth || 0,
        );
        const imageWidth = el
          .querySelector(".product-image")
          .getBoundingClientRect().width;
        if (theme.mediaMatches.md && zoomWidth - 75 > imageWidth) {
          el.addEventListener("mousemove", this.zoomEventListener);
          el.classList.remove("pointer-events-none");
        } else {
          el.removeEventListener("mousemove", this.zoomEventListener);
          el.classList.add("pointer-events-none");
        }
      });
    }

    /**
     * Handles mouse move over a zoomable image
     * @param {?object} evt - Event object.
     */
    handleZoomMouseMove(evt) {
      if (
        this.hasAttribute("data-zoom-enabled") &&
        !this.dataset.zoomTrigger &&
        !evt.target.closest(".gallery-zoom-modal")
      ) {
        return;
      }

      const hoverElem = evt.currentTarget;
      const zoomImage = hoverElem.querySelector(".js-zoom-image");

      // Download the zoom image if necessary
      if (zoomImage.dataset.src) {
        this.loadingSpinner.classList.remove("loading-spinner--out");

        const img = new Image();
        img.src = zoomImage.dataset.src;
        img.onload = () => {
          zoomImage.src = img.src;
          hoverElem.classList.remove("media--zoom-not-loaded");
          this.loadingSpinner.classList.add("loading-spinner--out");
        };
        zoomImage.removeAttribute("data-src");
      }

      try {
        const offsetX = evt.offsetX ? evt.offsetX : evt.touches[0].pageX;
        const offsetY = evt.offsetY ? evt.offsetY : evt.touches[0].pageY;
        const x = (offsetX / zoomImage.offsetWidth) * 100;
        const y = (offsetY / zoomImage.offsetHeight) * 100;
        zoomImage.style.objectPosition = `${x}% ${y}%`;
      } catch (err) {
        // Fail silently
      }
    }

    /**
     * Handles 'scroll' events on the main media container.
     */
    handleScroll() {
      if (this.viewerItemOffset <= 0) return;

      const newIndex = Math.round(
        this.viewer.scrollLeft / this.viewerItemOffset,
      );

      if (newIndex !== this.currentIndex && this.visibleItems.length > 1) {
        const viewerItemOffset =
          this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

        // If scroll wasn't caused by a resize event, update the active media.
        if (viewerItemOffset === this.viewerItemOffset) {
          this.customSetActiveMedia(this.visibleItems[newIndex], false);
        }
      }
    }

    /**
     * Handles 'click' events on the controls container.
     * @param {object} evt - Event object.
     */
    handleNavClick(evt) {
      const btn = evt.target.closest(".media-ctrl__btn");
      if (!btn) return;

      const newIndex =
        btn === this.nextBtn
          ? this.currentIndex + 1
          : this.currentIndex - 1;

      if (
        newIndex < 0 ||
        newIndex >= this.visibleItems.length ||
        this.visibleItems.length <= 1
      ) {
        return;
      }

      const itemToShow = this.visibleItems[newIndex];
      this.viewer.scrollTo({ left: itemToShow.offsetLeft, behavior: "smooth" });
    }

    /**
     * Handles 'click' events on the thumbnails container.
     * @param {object} evt - Event object.
     */
    handleThumbClick(evt) {
      const thumb = evt.target.closest("[data-media-id]");
      if (!thumb) return;

      const itemToShow = this.querySelector(
        `[data-media-id="${thumb.dataset.mediaId}"]`,
      );
      this.customSetActiveMedia(itemToShow);

      MediaGallery.playActiveMedia(itemToShow);
    }

    /**
     * Handles debounced 'resize' events on the window.
     */
    handleResize() {
      // Reset distance from leading edge of one slide to the next.
      if (this.visibleItems.length > 1) {
        this.viewerItemOffset =
          this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
      }

      if (this.thumbs && this.currentThumb) {
        this.checkThumbVisibilty(this.currentThumb);
      }
    }

    /**
     * Stub for variant-picker calls. Listening to change event instead.
     */
    // eslint-disable-next-line class-methods-use-this
    setActiveMedia() {}

    /**
     * Sets the active media item.
     * @param {Element} mediaItem - Media element to set as active.
     * @param {boolean} [scrollToItem=true] - Scroll to the active media item.
     */
    customSetActiveMedia(mediaItem, scrollToItem = true) {
      if (mediaItem === this.currentItem) return;
      window.pauseAllMedia(this);
      this.currentItem = mediaItem;
      this.currentIndex = this.visibleItems.indexOf(this.currentItem);

      if (this.dataset.layout === "stacked" && theme.mediaMatches.md) {
        // Update the active class and scroll to the active media
        if (this.stackedUnderline) {
          if (this.previousMediaItem)
            this.previousMediaItem.classList.remove("is-active");
          mediaItem.classList.add("is-active");
          this.previousMediaItem = mediaItem;
        }

        if (this.stackedScroll !== "never") {
          const y =
            mediaItem.getBoundingClientRect().top +
            document.documentElement.scrollTop -
            150;

          // If the element is far enough away to scroll to it
          if (Math.abs(y - document.documentElement.scrollTop) > 300) {
            window.scrollTo({
              top: y < 100 ? 0 : y,
              behavior: "smooth",
            });
          }
        }
        return;
      }

      if (scrollToItem)
        this.viewer.scrollTo({ left: this.currentItem.offsetLeft });
      if (this.thumbs) this.setActiveThumb();

      this.updateControls();

      this.announceLiveRegion(this.currentItem, this.currentIndex + 1);

      if (this.xrButton && mediaItem.dataset.mediaType === "model") {
        this.xrButton.dataset.shopifyModel3dId = mediaItem.dataset.mediaId;
      }
    }

    /**
     * Sets the active thumbnail.
     */
    setActiveThumb() {
      this.currentThumb = this.thumbs.querySelector(
        `[data-media-id="${this.currentItem.dataset.mediaId}"]`,
      );
      if (!this.currentThumb) return;

      const btn = this.currentThumb.querySelector("button");

      this.thumbs.querySelectorAll(".media-thumbs__btn").forEach((el) => {
        el.classList.remove("is-active");
        el.removeAttribute("aria-current");
      });

      btn.classList.add("is-active");
      btn.setAttribute("aria-current", "true");
      this.checkThumbVisibilty(this.currentThumb);
    }

    /**
     * Creates an array of the visible media items.
     */
    setVisibleItems() {
      this.viewerItems = this.querySelectorAll(".media-viewer__item");
      this.visibleItems = Array.from(this.viewerItems).filter(
        (el) => el.clientWidth > 0,
      );
      if (this.counterTotal) {
        this.counterTotal.textContent = this.visibleItems.length;
      }
    }

    /**
     * Ensures a thumbnail is in the visible area of the slider.
     * @param {Element} thumb - Thumb item element.
     */
    checkThumbVisibilty(thumb) {
      const scrollPos = this.thumbs.scrollLeft;
      const lastVisibleThumbOffset = this.thumbs.clientWidth + scrollPos;
      const thumbOffset = thumb.offsetLeft;

      if (
        thumbOffset + thumb.clientWidth > lastVisibleThumbOffset ||
        thumbOffset < scrollPos
      ) {
        this.thumbs.scrollTo({ left: thumbOffset, behavior: "smooth" });
      }
    }

    /**
     * Updates the media gallery status.
     * @param {Element} mediaItem - Active media element.
     * @param {number} index - Active media index.
     */
    announceLiveRegion(mediaItem, index) {
      const image = mediaItem.querySelector(".media-viewer img");
      if (!image) return;

      this.liveRegion.setAttribute("aria-hidden", "false");
      this.liveRegion.innerHTML = theme.strings.imageAvailable.replace(
        "[index]",
        index,
      );

      setTimeout(() => {
        this.liveRegion.setAttribute("aria-hidden", "true");
      }, 2000);
    }

    /**
     * Loads the deferred media for the active item.
     * @param {Element} mediaItem - Active media element.
     */
    static playActiveMedia(mediaItem) {
      window.pauseAllMedia();
      const deferredMedia = mediaItem.querySelector("deferred-media");
      if (deferredMedia) deferredMedia.loadContent();
    }
  }

  try {
    customElements.define("media-gallery", MediaGallery);
    MG("customElements.define('media-gallery', ...) complete");
  } catch (e) {
    if (e.name === "NotSupportedError") {
      MG("customElements.define threw NotSupportedError (already defined) — reinitializing existing elements");
    } else {
      console.error("[mg] Unexpected error defining custom element:", e);
      throw e;
    }
  }

  // Log any <media-gallery> elements already in the DOM.
  var existing = document.querySelectorAll("media-gallery");
  MG("elements found in DOM at define time:", existing.length,
    existing.length
      ? Array.from(existing).map(function (el) {
          return el.dataset.sectionId || "(no sectionId)";
        })
      : "(none)");

  // If the element was already registered, patch the old prototype with
  // every method from the current class so that existing upgraded elements
  // resolve to the latest code when they call this.someMethod().
  // Afterwards, force-reinitialize every existing <media-gallery> on the
  // page.  This ensures the gallery always picks up the latest code even
  // when HMR reloads the script but the custom element registry still
  // holds the previous definition.
  if (alreadyDefined && existing.length) {
    var OldClass = customElements.get("media-gallery");
    var newProto = MediaGallery.prototype;
    var oldProto = OldClass.prototype;

    MG("patching old prototype with new methods");
    Object.getOwnPropertyNames(newProto).forEach(function (key) {
      if (key !== "constructor") {
        try {
          var desc = Object.getOwnPropertyDescriptor(newProto, key);
          Object.defineProperty(oldProto, key, desc);
        } catch (e) {
          console.warn("[mg] Could not patch", key, "onto old prototype:", e);
        }
      }
    });

    MG("force-reinitializing", existing.length, "existing element(s)");
    Array.from(existing).forEach(function (el) {
      try {
        if (typeof el.init === "function") {
          el.init();
          MG("reinitialized", el.dataset.sectionId || el);
        }
      } catch (err) {
        console.error("[mg] Failed to reinitialize existing element:", el, err);
      }
    });
  }
})();
